import {
  ArrowRight,
  BarChart3,
  Bell,
  Bot,
  Clock,
  Database,
  FileText,
  FolderOpen,
  GitBranch,
  Lightbulb,
  ListTodo,
  Loader2,
  type LucideIcon,
  Mail,
  MessageSquare,
  MessagesSquare,
  PaintbrushVertical,
  Phone,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type FeatureFlag, featureFlags } from "@/config/featureFlags";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { agentService, chatService, dataSourceService, flowService, projectService, taskService } from "@/lib/services";
import type { Agent, ChatSession, ProjectSummary, TaskSummary } from "@/types";

// ---------------------------------------------------------------------------
// Icon resolver: maps icon name strings from the API to Lucide components
// ---------------------------------------------------------------------------
const AGENT_ICON_MAP: Record<string, LucideIcon> = {
  bot: Bot,
  phone: Phone,
  "file-text": FileText,
  search: Search,
  mail: Mail,
  "trending-up": TrendingUp,
  "list-todo": ListTodo,
  target: Target,
  zap: Zap,
  sparkles: Sparkles,
  bell: Bell,
  "bar-chart-3": BarChart3,
  "git-branch": GitBranch,
  database: Database,
  "message-square": MessageSquare,
  users: Users,
  lightbulb: Lightbulb,
  clock: Clock,
};

function resolveAgentIcon(name?: string): LucideIcon {
  if (!name) return Bot;
  return AGENT_ICON_MAP[name.toLowerCase()] ?? Bot;
}

interface DashboardStats {
  totalSessions: number;
  totalAgents: number;
  totalFlows: number;
  totalTasks: number;
  totalDataSources: number;
  activeTasks: number;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function StatSkeleton() {
  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-surface-secondary rounded-lg" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-20 bg-surface-secondary rounded-sm" />
        <div className="h-8 w-16 bg-surface-secondary rounded-sm" />
      </div>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <div className="flex items-start gap-4 p-4 bg-surface-secondary/50 rounded-lg animate-pulse">
      <div className="w-10 h-10 bg-surface-tertiary rounded-lg shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-3/4 bg-surface-tertiary rounded-sm" />
        <div className="h-3 w-1/2 bg-surface-tertiary rounded-sm" />
      </div>
    </div>
  );
}

export function Dashboard() {
  const { profile } = useAuth();
  const { teams, isLoadingTeams } = useApp();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentSessions, setRecentSessions] = useState<ChatSession[]>([]);
  const [topAgents, setTopAgents] = useState<Agent[]>([]);
  const [activeTasks, setActiveTasks] = useState<TaskSummary[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const user = profile?.user;
  const firstName = user?.displayName?.split(" ")[0] || "User";

  useEffect(() => {
    async function fetchDashboardData() {
      setLoading(true);
      try {
        const [
          sessionsRes,
          agentsRes,
          flowsRes,
          tasksRes,
          dataSourcesRes,
          activeTasksRes,
          recentSessionsRes,
          projectsRes,
        ] = await Promise.allSettled([
          chatService.listSessions({ pageSize: 1 }),
          agentService.list({ pageSize: 5 }),
          flowService.list({ pageSize: 1 }),
          taskService.list({ pageSize: 1 }),
          dataSourceService.list({ pageSize: 1 }),
          taskService.list({ pageSize: 5, status: "running" }),
          chatService.listSessions({ pageSize: 5 }),
          projectService.list({ pageSize: 10, status: "active" }),
        ]);

        const totalSessions = sessionsRes.status === "fulfilled" ? sessionsRes.value.meta.total : 0;
        const totalAgents = agentsRes.status === "fulfilled" ? agentsRes.value.meta.total : 0;
        const totalFlows = flowsRes.status === "fulfilled" ? flowsRes.value.meta.total : 0;
        const totalTasks = tasksRes.status === "fulfilled" ? tasksRes.value.meta.total : 0;
        const totalDataSources = dataSourcesRes.status === "fulfilled" ? dataSourcesRes.value.meta.total : 0;
        const activeTaskCount = activeTasksRes.status === "fulfilled" ? activeTasksRes.value.meta.total : 0;

        setStats({
          totalSessions,
          totalAgents,
          totalFlows,
          totalTasks,
          totalDataSources,
          activeTasks: activeTaskCount,
        });

        if (agentsRes.status === "fulfilled") {
          setTopAgents(agentsRes.value.data);
        }

        if (recentSessionsRes.status === "fulfilled") {
          setRecentSessions(recentSessionsRes.value.data);
        }

        if (activeTasksRes.status === "fulfilled") {
          setActiveTasks(activeTasksRes.value.data);
        }

        if (projectsRes.status === "fulfilled") {
          setProjects(projectsRes.value.data);
        }
      } catch {
        // Stats will remain null, showing a graceful empty state
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  const statCards = stats
    ? [
        {
          label: "Chat Sessions",
          value: stats.totalSessions.toLocaleString(),
          icon: MessageSquare,
          subtitle: "Total conversations",
          color: "from-brand-primary to-brand-primary-hover",
          onClick: () => navigate("/history"),
        },
        {
          label: "Agents",
          value: stats.totalAgents.toLocaleString(),
          icon: Bot,
          subtitle: `${stats.totalFlows} flow${stats.totalFlows === 1 ? "" : "s"} configured`,
          color: "from-brand-secondary to-brand-secondary-hover",
          onClick: () => navigate("/agents"),
        },
        {
          label: "Autopilot",
          value: stats.totalTasks.toLocaleString(),
          icon: ListTodo,
          subtitle: stats.activeTasks > 0 ? `${stats.activeTasks} running now` : "None running",
          color: "from-emerald-500 to-teal-600",
          onClick: () => navigate("/tasks"),
          featureFlag: "autopilot" as const,
        },
        {
          label: "Data Sources",
          value: stats.totalDataSources.toLocaleString(),
          icon: Database,
          subtitle: "Connected sources",
          color: "from-amber-500 to-orange-600",
          onClick: () => navigate("/data-sources"),
        },
      ]
    : [];

  const visibleStatCards = statCards.filter(
    (card) => !("featureFlag" in card) || !card.featureFlag || featureFlags[card.featureFlag],
  );

  return (
    <div className="p-6 lg:p-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-content-primary mb-2">Welcome back, {firstName}</h1>
        <p className="text-content-tertiary">Here's an overview of your AI workspace.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={`stat-skel-${i}`} />) // NOSONAR — index key safe for static skeleton placeholder list
          : visibleStatCards.map((stat) => {
              const Icon = stat.icon;
              return (
                <button
                  type="button"
                  key={stat.label}
                  onClick={stat.onClick}
                  className="bg-surface-primary border border-border-primary rounded-xl p-6 hover:border-border-secondary transition cursor-pointer group text-left"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`w-12 h-12 bg-linear-to-br ${stat.color} rounded-lg flex items-center justify-center`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-content-tertiary group-hover:text-content-secondary transition" />
                  </div>
                  <div>
                    <p className="text-content-tertiary text-sm mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-content-primary">{stat.value}</p>
                    <p className="text-xs text-content-quaternary mt-1">{stat.subtitle}</p>
                  </div>
                </button>
              );
            })}
      </div>

      {/* Your Teams & Projects */}
      {(() => {
        if (isLoadingTeams || loading) {
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Teams skeleton */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6 animate-pulse">
                <div className="h-5 w-28 bg-surface-secondary rounded-sm mb-4" />
                <div className="space-y-3">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={`team-skel-${i}`} className="flex items-center gap-3 p-3">
                      {" "}
                      {/* NOSONAR — index key safe for static skeleton placeholder */}
                      <div className="w-10 h-10 bg-surface-secondary rounded-lg shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-24 bg-surface-secondary rounded-sm" />
                        <div className="h-2 w-16 bg-surface-secondary rounded-sm" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Projects skeleton */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6 animate-pulse">
                <div className="h-5 w-28 bg-surface-secondary rounded-sm mb-4" />
                <div className="space-y-3">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={`proj-skel-${i}`} className="flex items-center gap-3 p-3">
                      {" "}
                      {/* NOSONAR — index key safe for static skeleton placeholder */}
                      <div className="w-10 h-10 bg-surface-secondary rounded-lg shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-24 bg-surface-secondary rounded-sm" />
                        <div className="h-2 w-16 bg-surface-secondary rounded-sm" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }
        if (teams.some((t) => !t.isEveryoneTeam) || projects.length > 0) {
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Teams */}
              {teams.some((t) => !t.isEveryoneTeam) && (
                <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-brand-secondary" />
                      <h2 className="text-lg font-bold text-content-primary">Your Teams</h2>
                    </div>
                    <button
                      onClick={() => navigate("/teams")}
                      className="text-sm text-brand-primary hover:text-brand-primary-hover transition"
                    >
                      View all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {teams
                      .filter((t) => !t.isEveryoneTeam)
                      .slice(0, 5)
                      .map((team) => (
                        <button
                          type="button"
                          key={team.uuid}
                          onClick={() => navigate(`/teams/${team.uuid}`)}
                          className="w-full flex items-center gap-3 p-3 bg-surface-secondary/50 rounded-lg hover:bg-interactive-hover transition cursor-pointer group text-left"
                        >
                          <div className="w-10 h-10 bg-brand-secondary/10 rounded-lg flex items-center justify-center shrink-0">
                            <Users className="w-5 h-5 text-brand-secondary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-content-primary font-medium text-sm truncate">{team.name}</p>
                            <p className="text-xs text-content-tertiary">
                              {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-content-quaternary group-hover:text-content-secondary transition shrink-0" />
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              {projects.length > 0 && (
                <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-brand-primary" />
                      <h2 className="text-lg font-bold text-content-primary">Your Projects</h2>
                    </div>
                    <button
                      onClick={() => navigate("/projects")}
                      className="text-sm text-brand-primary hover:text-brand-primary-hover transition"
                    >
                      View all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {projects.slice(0, 5).map((project) => (
                      <button
                        type="button"
                        key={project.uuid}
                        onClick={() => navigate(`/projects/${project.uuid}`)}
                        className="w-full flex items-center gap-3 p-3 bg-surface-secondary/50 rounded-lg hover:bg-interactive-hover transition cursor-pointer group text-left"
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${project.color || "#EC622B"}20` }}
                        >
                          {(() => {
                            const ProjectIcon = project.icon
                              ? (AGENT_ICON_MAP[project.icon.toLowerCase()] ?? FolderOpen)
                              : FolderOpen;
                            return <ProjectIcon className="w-5 h-5" style={{ color: project.color || "#EC622B" }} />;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-content-primary font-medium text-sm truncate">{project.name}</p>
                          <div className="flex items-center gap-2 text-xs text-content-tertiary">
                            <span>
                              {project.memberCount} member{project.memberCount === 1 ? "" : "s"}
                            </span>
                            <span>·</span>
                            <span>
                              {project.resourceCount} resource{project.resourceCount === 1 ? "" : "s"}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-content-quaternary group-hover:text-content-secondary transition shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }
        return null;
      })()}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* What do you want to do? — Unified entry point for all 5 AI modes */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <h2 className="text-xl font-bold text-content-primary mb-1">What do you want to do?</h2>
            <p className="text-sm text-content-quaternary mb-5">Pick the right tool for the job.</p>
            <div className="space-y-3">
              {[
                {
                  icon: MessageSquare,
                  label: "Chat",
                  desc: "Ask anything",
                  path: "/chat",
                  color: "from-brand-primary to-brand-primary-hover",
                  action: "Start Chat",
                  featureFlag: undefined as FeatureFlag | undefined,
                },
                {
                  icon: MessagesSquare,
                  label: "Brainstorm",
                  desc: "Get multiple perspectives on a problem",
                  path: "/group-chat",
                  color: "from-blue-500 to-blue-600",
                  action: "Start",
                  featureFlag: "brainstorm" as FeatureFlag | undefined,
                },
                {
                  icon: ListTodo,
                  label: "Autopilot",
                  desc: "Hand off a goal and let AI do the work",
                  path: "/tasks",
                  color: "from-emerald-500 to-teal-600",
                  action: "Create Goal",
                  featureFlag: "autopilot" as FeatureFlag | undefined,
                },
                {
                  icon: PaintbrushVertical,
                  label: "Canvas",
                  desc: "Generate documents, diagrams & visuals",
                  path: "/canvas",
                  color: "from-purple-500 to-purple-600",
                  action: "Create",
                  featureFlag: "canvas" as FeatureFlag | undefined,
                },
                {
                  icon: GitBranch,
                  label: "Workflows",
                  desc: "Build repeatable multi-step processes",
                  path: "/flows",
                  color: "from-brand-secondary to-brand-secondary-hover",
                  action: "Build",
                  featureFlag: "workflows" as FeatureFlag | undefined,
                },
              ]
                .filter((item) => !item.featureFlag || featureFlags[item.featureFlag])
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-interactive-hover transition text-left group border border-border-primary hover:border-border-secondary"
                    >
                      <div
                        className={`w-10 h-10 bg-linear-to-br ${item.color} rounded-lg flex items-center justify-center shrink-0`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-content-primary">{item.label}</p>
                        <p className="text-xs text-content-quaternary">{item.desc}</p>
                      </div>
                      <span className="text-xs font-medium text-brand-primary opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                        {item.action} &rarr;
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Active Autopilot Goals */}
          {!loading && activeTasks.length > 0 && (
            <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-content-primary">Active Goals</h2>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                    {activeTasks.length} running
                  </span>
                </div>
                <button
                  onClick={() => navigate("/tasks")}
                  className="text-sm text-brand-primary hover:text-brand-primary-hover transition"
                >
                  View all
                </button>
              </div>
              <div className="space-y-3">
                {activeTasks.map((task) => (
                  <button
                    type="button"
                    key={task.uuid}
                    onClick={() => navigate(`/tasks/${task.uuid}`)}
                    className="w-full flex items-start gap-4 p-4 bg-surface-secondary/50 rounded-lg hover:bg-interactive-hover transition cursor-pointer text-left"
                  >
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center shrink-0">
                      <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-content-primary font-medium mb-1 truncate">{task.title}</p>
                      <div className="flex items-center gap-2 text-sm text-content-tertiary">
                        {task.agentName && (
                          <>
                            <span className="truncate max-w-[120px]">{task.agentName}</span>
                            <span>·</span>
                          </>
                        )}
                        <span>
                          {task.stepCount} step{task.stepCount === 1 ? "" : "s"}
                        </span>
                        {task.startedAt && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <Clock className="w-3 h-3" />
                              Started {timeAgo(task.startedAt)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Conversations */}
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-content-primary">Recent Conversations</h2>
              <button
                onClick={() => navigate("/history")}
                className="text-sm text-brand-primary hover:text-brand-primary-hover transition"
              >
                View all
              </button>
            </div>
            <div className="space-y-3">
              {(() => {
                if (loading) {
                  return Array.from({ length: 4 }).map((_, i) => <ActivitySkeleton key={`activity-skel-${i}`} />); // NOSONAR — index key safe for static skeleton placeholder list
                }
                if (recentSessions.length > 0) {
                  return recentSessions.map((session) => (
                    <button
                      type="button"
                      key={session.uuid}
                      onClick={() => navigate(`/chat/${session.uuid}`)}
                      className="w-full flex items-start gap-4 p-4 bg-surface-secondary/50 rounded-lg hover:bg-interactive-hover transition cursor-pointer text-left"
                    >
                      <div className="w-10 h-10 bg-surface-tertiary rounded-lg flex items-center justify-center shrink-0">
                        {session.agentName ? (
                          <Bot className="w-5 h-5 text-brand-secondary" />
                        ) : (
                          <MessageSquare className="w-5 h-5 text-brand-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-content-primary font-medium mb-1 truncate">
                          {session.title || "Untitled Chat"}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-content-tertiary">
                          {session.agentName && (
                            <>
                              <span className="truncate max-w-[120px]">{session.agentName}</span>
                              <span>·</span>
                            </>
                          )}
                          <span>
                            {session.messageCount} message{session.messageCount === 1 ? "" : "s"}
                          </span>
                          <span>·</span>
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <Clock className="w-3 h-3" />
                            {timeAgo(session.updatedAt)}
                          </span>
                        </div>
                      </div>
                    </button>
                  ));
                }
                return (
                  <div className="text-center py-8">
                    <MessageSquare className="w-8 h-8 text-content-quaternary mx-auto mb-3" />
                    <p className="text-content-tertiary text-sm">No conversations yet</p>
                    <button
                      onClick={() => navigate("/chat")}
                      className="mt-3 text-sm text-brand-primary hover:text-brand-primary-hover transition"
                    >
                      Start your first chat
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Top Agents */}
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-content-primary">Your Agents</h2>
              <button
                onClick={() => navigate("/agents")}
                className="text-sm text-brand-primary hover:text-brand-primary-hover transition"
              >
                View all
              </button>
            </div>
            <div className="space-y-3">
              {(() => {
                if (loading) {
                  return Array.from({ length: 3 }).map((_, i) => (
                    <div key={`agent-skel-${i}`} className="flex items-center gap-3 p-3 animate-pulse">
                      {" "}
                      {/* NOSONAR — index key safe for static skeleton placeholder */}
                      <div className="w-10 h-10 bg-surface-secondary rounded-lg shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-24 bg-surface-secondary rounded-sm" />
                        <div className="h-2 w-16 bg-surface-secondary rounded-sm" />
                      </div>
                    </div>
                  ));
                }
                if (topAgents.length > 0) {
                  return topAgents.map((agent) => {
                    const agentColor = agent.color || "#7C16F5";
                    return (
                      <button
                        type="button"
                        key={agent.uuid}
                        className="w-full flex items-center gap-3 p-3 bg-surface-secondary/50 rounded-lg hover:bg-interactive-hover transition cursor-pointer text-left"
                        onClick={() => navigate(`/chat?agent=${agent.uuid}`)}
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${agentColor}20` }}
                        >
                          {(() => {
                            const IconComponent = resolveAgentIcon(agent.icon);
                            return <IconComponent className="w-5 h-5" style={{ color: agentColor }} />;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-content-primary font-medium text-sm truncate">{agent.name}</p>
                          <p className="text-xs text-content-tertiary">
                            {agent.usageCount} use{agent.usageCount === 1 ? "" : "s"}
                          </p>
                        </div>
                      </button>
                    );
                  });
                }
                return (
                  <div className="text-center py-6">
                    <Bot className="w-8 h-8 text-content-quaternary mx-auto mb-3" />
                    <p className="text-content-tertiary text-sm">No agents yet</p>
                    <button
                      onClick={() => navigate("/agents")}
                      className="mt-2 text-sm text-brand-primary hover:text-brand-primary-hover transition"
                    >
                      Create your first agent
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
